import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, type BadgeTone } from '@artosera/ui';
import type { LotRecette, RecetteOuverture } from '../../../../types.js';
import pageStyles from '../../../../styles/page.module.css';
import styles from './Recette.module.css';

const TON_LOT: Record<string, BadgeTone> = {
  en_cours: 'reserved',
  enfile: 'reserved',
  echoue: 'sold',
};

/**
 * L'état des lots de la galerie, à l'instant : ce qui est exécuté, annulé ou
 * mis de côté, et ce qui retient encore la recette (lots lancés et pas finis,
 * lots jamais lancés, plan actif). Chaque lot renvoie à sa fiche.
 */
export function EtatLots({
  lots,
  galleryId,
}: {
  lots: RecetteOuverture['lots'];
  galleryId: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const cle = 'ouvertures.fiche.recette.lots';
  const retient = lots.enCours.length > 0 || lots.nonLances.length > 0 || lots.planActif;

  const compteurs: Array<{ nom: string; valeur: string; retient?: boolean }> = [
    { nom: 'executes', valeur: String(lots.executes) },
    { nom: 'annules', valeur: String(lots.annules) },
    { nom: 'misDeCote', valeur: String(lots.misDeCote.length) },
    { nom: 'enCours', valeur: String(lots.enCours.length), retient: lots.enCours.length > 0 },
    { nom: 'nonLances', valeur: String(lots.nonLances.length), retient: lots.nonLances.length > 0 },
    {
      nom: 'plan',
      valeur: t(`${cle}.${lots.planActif ? 'planActif' : 'planInactif'}`),
      retient: lots.planActif,
    },
  ];

  const nature = (lot: LotRecette): string =>
    t(`reprise.natures.${lot.nature}`, { defaultValue: lot.nature });
  const lien = (lot: LotRecette): JSX.Element => (
    <Link
      to={`/galeries/${galleryId}/reprise?lot=${encodeURIComponent(lot.id)}`}
      aria-label={t(`${cle}.ouvrir`, { nature: nature(lot) })}
    >
      {nature(lot)}
    </Link>
  );
  const etat = (lot: LotRecette): JSX.Element => (
    <Badge tone={TON_LOT[lot.etat] ?? 'neutral'}>
      {t(`reprise.etats.${lot.etat}`, { defaultValue: lot.etat })}
    </Badge>
  );

  return (
    <section className={pageStyles.panneau} aria-labelledby="rec-lots">
      <div className={pageStyles.sectionTitre}>
        <h2 id="rec-lots">{t(`${cle}.titre`)}</h2>
      </div>

      <dl className={styles.compteurs}>
        {compteurs.map((compteur) => (
          <div key={compteur.nom} className={styles.compteur} data-retient={compteur.retient}>
            <dt>{t(`${cle}.${compteur.nom}`)}</dt>
            <dd>{compteur.valeur}</dd>
          </div>
        ))}
      </dl>

      {retient ? (
        <p className={styles.alerte} role="status">
          {t(`${cle}.retenus`)}
        </p>
      ) : (
        <p className={styles.texte}>{t(`${cle}.tousTermines`)}</p>
      )}

      {lots.enCours.length > 0 ? (
        <>
          <h3 className={styles.titreBloc}>{t(`${cle}.listeEnCours`)}</h3>
          <ul className={styles.lotsListe}>
            {lots.enCours.map((lot) => (
              <li key={lot.id} className={styles.lot}>
                {lien(lot)}
                {etat(lot)}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {lots.nonLances.length > 0 ? (
        <>
          <h3 className={styles.titreBloc}>{t(`${cle}.listeNonLances`)}</h3>
          <ul className={styles.lotsListe}>
            {lots.nonLances.map((lot) => (
              <li key={lot.id} className={styles.lot}>
                {lien(lot)}
                {etat(lot)}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {lots.misDeCote.length > 0 ? (
        <>
          <h3 className={styles.titreBloc}>{t(`${cle}.listeMisDeCote`)}</h3>
          <ul className={styles.lotsListe}>
            {lots.misDeCote.map((lot) => (
              <li key={lot.id} className={styles.lot}>
                {lien(lot)}
                {etat(lot)}
                <p className={styles.lotMotif}>{lot.motif}</p>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
